import React, { useState, useEffect } from 'react';
import ModalContainer from 'components/shared/modal/modal_container';
import RenderInBody from 'components/shared/render_in_body';
import { useQueryClient } from '@tanstack/react-query';
import { Button, ButtonGroup, Icon } from 'ui';
import { Text, TEXT_VARIANTS } from 'components/ui/Typography';
import { AlertCircle } from 'react-feather';
import { REACT_QUERY_STATUS } from 'constants/config';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import { useUpdateGlobalSkillsSettings } from './queries/globalSkillsSettings';

const ConfirmationModal = ({
  saveClicked,
  closeModal,
  tempIconChoice,
  tempRatingDefinition,
  tempRatingScale,
  setEditMode,
  currentCompanyRatingScale,
  setSaveSuccess,
  trimTempRatingDefinitions,
}) => {
  const queryClient = useQueryClient();
  const { mutate, status } = useUpdateGlobalSkillsSettings({
    ratingIconStyle: '',
    ratingDefinitions: {},
    ratingScale: '',
  });
  const [saveFailure, setSaveFailure] = useState(false);

  function handleSubmit() {
    trimTempRatingDefinitions();
    const newRatingDefinitions = Object.fromEntries(
      Object.entries(tempRatingDefinition).slice(0, tempRatingScale),
    );
    try {
      mutate({
        ratingIconStyle: tempIconChoice,
        ratingDefinitions: JSON.stringify(newRatingDefinitions),
        ratingScale: tempRatingScale,
      });
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (status === REACT_QUERY_STATUS.SUCCESS) {
      queryClient.invalidateQueries('skillsSettings');
      setEditMode(false);
      setSaveSuccess(true);
      closeModal();
    }
    if (status === REACT_QUERY_STATUS.ERROR) {
      setSaveFailure(true);
    }
  }, [status, closeModal, queryClient, setEditMode, setSaveSuccess]);

  return (
    <RenderInBody>
      <ModalContainer
        className="ModalTakeover-background Modal-background d-f ai-c"
        onEscape={closeModal}
      >
        {saveClicked && (
          <div className="ModalTakeover-container w-45p">
            <div className="TemplateCreationModal-body">
              <div className="TemplateCreationModal-title">
                {t('settings.tab.global_skills.confirmation_modal_header')}
              </div>
              {saveFailure && (
                <div className="ErrorContainer--brandRed h-60 d-f ai-c mt-20 mb-24">
                  <AlertCircle size={15} className="ml-16 mr-12" />
                  <div className="fw-400 fz-14 llui-color-error-dark">
                    {t('settings.tab.global_skills.error_message')}
                  </div>
                </div>
              )}
              <div className="mt-16">
                <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'settings.tab.global_skills.confirmation_modal_body',
                        {
                          skill_level_number: currentCompanyRatingScale,
                          skill_level_number_new: tempRatingScale,
                        },
                      ),
                    }}
                  />
                </Text>
              </div>
              <div className="mt-16">
                <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t(
                        'settings.tab.global_skills.confirmation_modal_note',
                        {
                          skill_level_number_new: tempRatingScale,
                        },
                      ),
                    }}
                  />
                </Text>
              </div>
              <button
                type="button"
                aria-label={t('common.close')}
                className="u-resetBtn ModalTakeover-closeButton"
                onClick={closeModal}
              >
                <Icon name="error" />
              </button>
              <div className="mt-24">
                <ButtonGroup alignEnd>
                  <Button type="cancel" onClick={closeModal}>
                    {t('common.cancel')}
                  </Button>
                  <Button
                    className="TemplateCreationModal-submitButton"
                    onClick={handleSubmit}
                  >
                    {t('common.save_changes')}
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          </div>
        )}
      </ModalContainer>
    </RenderInBody>
  );
};

export default withSkillsClientWrapper(
  withReactQueryWrapper(ConfirmationModal),
);
